import { useEffect, useState } from 'react'
import { authedFetch } from '@api'
import { getToken, TOKEN_AUTH } from '@auth/token.ts'
import { API_BASE } from '@config'

type Kind = 'recipes' | 'activities'

// The stored URL on the row is a private S3 URL the browser must never fetch
// directly — photos go through the authed, household-scoped proxy
// (`GET /{kind}/{id}/image`) instead.
//
// The `?v=` cache-buster is copied through from the stored URL: the proxy
// serves `immutable`, so a replace needs a new path to force a refetch.
// Null when there's no photo, so callers fall through to a placeholder.
const proxyPath = (
	kind: Kind,
	id: string,
	storedUrl: string | null,
): string | null => {
	if (!storedUrl) return null
	let version = ''
	try {
		const v = new URL(storedUrl).searchParams.get('v')
		if (v) version = `?v=${encodeURIComponent(v)}`
	} catch {
		// Non-URL stored value — serve without a cache-buster.
	}
	return `/${kind}/${id}/image${version}`
}

// Website: the proxy URL itself. The session cookie rides on it (same-site in
// prod, same-origin via the Vite proxy in dev).
const useProxyImageSrc = (
	kind: Kind,
	id: string,
	storedUrl: string | null,
): string | null => {
	const path = proxyPath(kind, id, storedUrl)
	return path === null ? null : `${API_BASE}${path}`
}

// iOS build: an <img> can't send the Bearer header, so the photo is fetched
// and shown through an object URL. One entry per photo, kept for the session
// so lists and detail pages don't refetch.
type Entry = { path: string; src: Promise<string | null> }
const cache = new Map<string, Entry>()
let cacheToken: string | null = null

const revoke = (entry: Entry) => {
	void entry.src.then((src) => {
		if (src) URL.revokeObjectURL(src)
	})
}

const loadImage = async (item: string, path: string) => {
	// A different session must not be shown the last one's photos.
	const token = await getToken()
	if (token !== cacheToken) {
		cache.forEach(revoke)
		cache.clear()
		cacheToken = token
	}

	const hit = cache.get(item)
	if (hit?.path === path) return hit.src
	// Replaced photo: the old version won't be asked for again.
	if (hit) revoke(hit)

	const entry: Entry = {
		path,
		src: authedFetch(path)
			.then(async (res) =>
				res.ok ? URL.createObjectURL(await res.blob()) : null,
			)
			.catch(() => null),
	}
	cache.set(item, entry)
	const src = await entry.src
	// Don't keep a failure, so the next mount retries.
	if (src === null && cache.get(item) === entry) cache.delete(item)
	return src
}

const useFetchedImageSrc = (
	kind: Kind,
	id: string,
	storedUrl: string | null,
): string | null => {
	const path = proxyPath(kind, id, storedUrl)
	const [loaded, setLoaded] = useState<{
		path: string
		src: string | null
	} | null>(null)

	useEffect(() => {
		if (path === null) return
		let cancelled = false
		void loadImage(`${kind}/${id}`, path).then((src) => {
			if (!cancelled) setLoaded({ path, src })
		})
		return () => {
			cancelled = true
		}
	}, [kind, id, path])

	// Null until this path has loaded, so a replaced photo doesn't linger.
	return loaded?.path === path ? loaded.src : null
}

// Resolve the `<img src>` for a recipe/activity photo.
export const useImageSrc = TOKEN_AUTH ? useFetchedImageSrc : useProxyImageSrc
